import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from 'react-bootstrap';
import Grid from '@mui/material/Grid'; // TODO: try to remove this as its from material
import {
  reloadFilters,
  setModalQuestionnaire,
  updateModalQuestionValue,
} from 'src/redux/actions/questionnaireActions';
import {
  handleQuestionRender,
  prepareResponseData,
  sanitiseHTML,
} from 'src/utils/helpers';
import AppButton from 'src/components/base/Button/Button';
import { TypeAheadLocationSearch, LoadingIndicator, Typography } from 'src/latest/src/components';
import DropdownContainer from 'src/containers/QuestionnaireParent/QuestionOptionsLibrary/components/DropDownMenu/DropDownContainer';
import { evaluateSubmitButtonStatus } from 'src/latest/src/utils';
import InfoMessage from 'src/containers/QuestionnaireParent/QuestionOptionsLibrary/components/InfoMessage/InfoMessage';
import {
  fetchQuestionsOfModal,
  saveQuestionResponsesOfPage,
} from 'src/redux/services/questionnaireServices';
import {
  ModalQuestionnaire,
  PageQuestion,
  PageQuestionChoice,
} from 'src/latest/src/types';
import './ClientOnboardingModal.scss';
import { ModalQuestionnairePageIds, QuestionsUniqueKeys } from 'src/containers/QuestionnaireParent/QuestionnaireEnum';

const pageId = ModalQuestionnairePageIds.patientAgeGroupInsuranceZipCodePage;

export default function ClientOnboardingModal() {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showModal, setShowModal] = useState<boolean>(true);
  const [submitButtonEnabled, setSubmitButtonEnabled] =
    useState<boolean>(false);
  const [questionnaireLoading, setQuestionnaireLoading] =
    useState<boolean>(false);
  const { questionnaireSessionId, modalQuestions, modalPageMetadata } =
    useSelector((state: any) => state?.questionnaireData?.currentConfig);

  useEffect(() => {
    fetchQuestionnaireData();
  }, []);

  useEffect(() => {
    if (modalQuestions && modalQuestions?.length) {
      const _submitButtonStatus = evaluateSubmitButtonStatus(
        modalQuestions,
        modalPageMetadata,
      );

      setSubmitButtonEnabled(_submitButtonStatus);
    }
  }, [modalQuestions, modalPageMetadata]);

  const fetchQuestionnaireData = async () => {
    const reponse: ModalQuestionnaire = await fetchQuestionsOfModal(pageId);

    if (reponse?.questions) {
      dispatch(setModalQuestionnaire(reponse));
      setIsLoading(false);
    }
  };

  const handleOnclick = async () => {
    const response = prepareResponseData(modalQuestions);

    if (response) {
      setQuestionnaireLoading(true);

      await saveQuestionResponsesOfPage(
        pageId,
        questionnaireSessionId,
        response,
      );

      dispatch(reloadFilters(true));
      setShowModal(false);
    }
  };

  const dropdownOnSelected = (question: PageQuestion, value: Array<any>) => {
    if (
      question.unique_key === QuestionsUniqueKeys.patientsAgeSelfMatchingClientQuestionV1 &&
      value
    ) {
      const selectedValue = value.filter(filter => filter.selected);

      if (selectedValue && selectedValue.length) {
        const ageRangeValue = selectedValue[0].raw_metadata;

        localStorage.setItem('age_range', ageRangeValue || '');
      }
    }

    dispatch(updateModalQuestionValue(question?.id, '', value));
  };

  const onLocationSelection = (
    questionId: string,
    value: object,
    choiceId: string,
  ) => {
    const isValid = !!value;
    const valueInString = value ? JSON.stringify(value) : '';

    dispatch(
      updateModalQuestionValue(questionId, choiceId, valueInString, isValid),
    );
  };

  const options = (question: PageQuestion) => {
    switch (question?.metadata?.question_type) {
      case 'dropdown':
        return (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <DropdownContainer
              className=""
              title={question.display_title}
              onChangeValue={(choices: PageQuestionChoice[]) => {
                dropdownOnSelected(question, choices);
              }}
              infoMessage={question.info_message}
              menu={question?.choices}
              fromQuestionnaire={true}
            />
          </div>
        );
      case 'location_search':
        return (
          <TypeAheadLocationSearch
            choices={question?.choices}
            onLocationSelection={(
              choice: PageQuestionChoice,
              choiceId: string,
            ) => {
              onLocationSelection(question.id, choice, choiceId);
            }}
            type="questionnaire"
          />
        );
      default:
        break;
    }
  };

  return (
    <Modal
      backdrop="static"
      show={showModal}
      dialogClassName="client-onboarding-modal modal-70w modal-70h"
      centered={true}
    >
      <Modal.Header
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
        }}
      >
        <Modal.Title className="d-flex">
          <Typography color="color-28" variant="size-20">
            Please answer the following questions
          </Typography>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ backgroundColor: '#f0f2f8' }}>
        <LoadingIndicator
          loading={isLoading}
          style={{
            height: '300px',
          }}
        />
        <div
          style={{
            width: '100%',
            margin: 'auto',
            display: 'flex',
            padding: '16px',
          }}
        >
          {!isLoading && modalQuestions?.length > 0 && (
            <Grid
              container={true}
              rowSpacing={1}
              spacing={4}
              sx={{ maxWidth: '830px', margin: 'auto' }}
            >
              {modalQuestions &&
                modalQuestions.map((question: PageQuestion) => {
                  // conditional question rendering - question level , not choice level
                  const renderCurrent = handleQuestionRender(
                    question,
                    modalQuestions,
                  );

                  return (
                    renderCurrent && (
                      <Grid
                        item={true}
                        sm={
                          question?.metadata?.question_layout === 'grid'
                            ? 6
                            : 12
                        }
                        justifyContent={
                          question?.metadata?.question_layout === 'grid'
                            ? 'flex-start'
                            : 'center'
                        }
                        className="question-container mt-2"
                        key={question.id}
                        id={question.id}
                      >
                        {question?.title &&
                          question?.metadata?.hide_question_title !==
                          'true' && (
                            <div className="question-title-container">
                              {question?.title && (
                                <div className='d-flex question-header mb-3 mt-3 justify-content-center align-items-center'>
                                  <Typography
                                  >
                                    {sanitiseHTML(question?.title) || ''}
                                  </Typography>
                                  {question?.info_message && question?.unique_key ===
                                    QuestionsUniqueKeys.patientLocation && (
                                      <div style={{ marginLeft: '8px', marginTop: '-4px' }} >
                                        <InfoMessage text={question?.info_message} />
                                      </div>
                                    )}
                                </div>
                              )}
                              {question.description && (
                                <Typography
                                  className={`question-description mt-3 mb-3 text-center`}
                                >
                                  {sanitiseHTML(question?.description) || ''}
                                </Typography>
                              )}
                            </div>
                          )}
                        {options(question)}
                      </Grid>
                    )
                  );
                })}

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  marginTop: '48px',
                  width: '100%',
                }}
              >
                <AppButton
                  id="questionnaire-modal-continue-button"
                  className="primary no-border"
                  disabled={questionnaireLoading || !submitButtonEnabled}
                  onClick={() => {
                    handleOnclick();
                  }}
                  label="Continue"
                  isProcessing={questionnaireLoading}
                />
              </div>
            </Grid>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
}
